import React, { useMemo } from 'react';
import FiltersSidebar, {
  FilterSection,
  FilterCheckboxRow,
  FilterCheckboxList,
} from './FiltersSidebar';
import type {
  ProductType,
  UseProductGroupFiltersResult,
} from '../hooks/useProductGroupFilters';

interface ProductGroupRow {
  productType: ProductType | string;
}

interface ProductGroupFiltersSidebarProps {
  filters: UseProductGroupFiltersResult;
  /** Source list — used to derive per-type counts in the right column. */
  groups: ProductGroupRow[];
  collapsed: boolean;
}

const TYPE_OPTIONS: { value: ProductType; label: string }[] = [
  { value: 'rental', label: 'Rental' },
  { value: 'service', label: 'Service' },
  { value: 'sales_item', label: 'Sale Item' },
];

const ProductGroupFiltersSidebar: React.FC<ProductGroupFiltersSidebarProps> = ({
  filters,
  groups,
  collapsed,
}) => {
  const { state, setField, clearAll, activeFilterCount, hasActiveFilters } = filters;

  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of groups) {
      m.set(g.productType, (m.get(g.productType) || 0) + 1);
    }
    return m;
  }, [groups]);

  const toggleType = (t: ProductType) => {
    const has = state.productTypes.includes(t);
    setField(
      'productTypes',
      has ? state.productTypes.filter((x) => x !== t) : [...state.productTypes, t],
    );
  };

  return (
    <FiltersSidebar
      collapsed={collapsed}
      activeFilterCount={activeFilterCount}
      hasActiveFilters={hasActiveFilters}
      onClearAll={clearAll}
      onRequestClose={() => setField('sidebarCollapsed', true)}
    >
      <FilterSection label="Product type">
        <FilterCheckboxList>
          {TYPE_OPTIONS.map((opt) => (
            <FilterCheckboxRow
              key={opt.value}
              checked={state.productTypes.includes(opt.value)}
              onChange={() => toggleType(opt.value)}
              label={opt.label}
              count={typeCounts.get(opt.value) ?? 0}
            />
          ))}
        </FilterCheckboxList>
      </FilterSection>
    </FiltersSidebar>
  );
};

export default ProductGroupFiltersSidebar;
